import { apiClient } from "@/api/api-client";
import createBaseService, { type Pagination } from "@/api/base-service.ts";
import type { Counsel, CounselFormSchema } from "@/features/case-evaluation/types/counsel";
import type { CaseFilingFormSchema } from "@/features/dashboard/types/case-type";
import type { UserFormSchema, UserPostAsFormSchema, isLeadFormSchema } from "@/features/dashboard/types/user";
import type { CaseRecordUser } from "../../types/case-record-users";
import type { CaseDefendant, CaseDefendantSchema, CaseRecord, CaseStageChartSchema } from "../../types/case-records";

const caseRecordService = {
	...createBaseService<CaseRecord>("/backoffice/case-records"),

	listCases: async (branchId?: string, params: Record<string, any> = {}) => {
		const response = await apiClient.get<{ data: any[]; meta: Pagination }>("/backoffice/case-records/list-cases", {
			params: {
				...params,
				...(branchId && { "filter[branch_id]": branchId }),
			},
		});
		return response.data;
	},

	updateIsLead: async (data: isLeadFormSchema) => {
		const response = await apiClient.put<{ data: CaseRecordUser }>(`/backoffice/case-records/${data.id}/is_lead_counsel`, data);

		console.log(response.data, "test");
		return response.data.data;
	},

	addDefendant: async (data: CounselFormSchema) => {
		const response = await apiClient.post<{ data: Counsel }>("/backoffice/case-records/add-defendant", data);

		return response.data.data;
	},

	editAddress: async (data: CounselFormSchema) => {
		const response = await apiClient.post<{ data: Counsel }>("/backoffice/case-records/edit-address", data);

		return response.data.data;
	},

	editName: async (data: CounselFormSchema) => {
		const response = await apiClient.post<{ data: Counsel }>("/backoffice/case-records/edit-name", data);

		return response.data.data;
	},

	setBranch: async (data: any) => {
		const response = await apiClient.post<{ data: any }>("/backoffice/set-branch", data);

		return response.data.data;
	},

	updateTsn: async (data: any) => {
		const response = await apiClient.post<{ data: any }>("/update-tsn", data);

		return response.data.data;
	},

	updateSheriffReport: async (data: any) => {
		const response = await apiClient.post<{ data: any }>("/update-sheriff-report", data);

		return response.data.data;
	},

	custom: async (data: CounselFormSchema) => {
		const response = await apiClient.post<{ data: Counsel }>("/backoffice/case-records/add-custom-counsel", data);

		return response.data.data;
	},

	partyComplaint: async (data: CounselFormSchema) => {
		const response = await apiClient.post<{ data: Counsel }>("/backoffice/case-records/party-complaint", data);

		return response.data.data;
	},

	upsert: async (data: CaseFilingFormSchema) => {
		const id = data?.id;

		if (id) {
			const response = await apiClient.post<{ data: CaseRecord }>(`/backoffice/case-records/${id}`, data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			return response.data.data;
		}

		const response = await apiClient.post<{ data: CaseRecord }>("/backoffice/case-records", data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});

		return response.data.data;
	},

	lisyBySearch: async (params = {}) => {
		const response = await apiClient.get<{ data: T[]; meta: Pagination }>("/case-record-search", { params });

		return response.data;
	},
	updateStatusById: async (data: UserFormSchema) => {
		const response = await apiClient.put<{ data: CaseRecord }>(`/backoffice/case-records/${data.id}/update-status`, data);

		return response.data.data;
	},

	updatePostAsById: async (data: UserPostAsFormSchema) => {
		const response = await apiClient.put<{ data: CaseRecord }>(`/backoffice/case-records/${data.id}/update-post-as`, data);

		return response.data.data;
	},

	updateCaseOverview: async (caseRecordId?: string, data?: Record<string, unknown>) => {
		const response = await apiClient.post("/update-case-overview", {
			caseRecordId,
			...data,
		});

		return response.data.data;
	},

	removeCounsel: async (caseRecordId?: string, data?: Record<string, unknown>) => {
		const response = await apiClient.post("/remove-counsel", {
			caseRecordId,
			...data,
		});

		return response.data.data;
	},

	regenerateKeyCode: async (caseRecordId?: string, data?: Record<string, unknown>) => {
		const response = await apiClient.post("/regenerate-key-code", {
			caseRecordId,
			...data,
		});

		return response.data.data;
	},

	replaceCounsel: async (caseRecordId?: string, data?: Record<string, unknown>) => {
		const response = await apiClient.post("/replace-counsel", {
			caseRecordId,
			...data,
		});

		return response.data.data;
	},

	updateDefendantById: async (data: CaseDefendantSchema) => {
		let response = null;

		if (data.id) {
			response = await apiClient.put<{ data: CaseDefendant }>(`/case-defendants/${data.id}`, data);
		} else {
			response = await apiClient.post<{ data: CaseDefendant }>("/case-defendants", data);
		}

		return response.data.data;
	},

	updateDefendantCounselById: async (data: UserFormSchema) => {
		const response = await apiClient.put<{ data: CaseRecord }>(`/backoffice/case-records/${data.id}/update-defendant`, data);

		return response.data.data;
	},

	upsertById: async (data: T) => {
		const id = data.id;
		if (id) {
			const response = await apiClient.post<{ data: T }>(`/case-records/${data.id}/files`, data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			return response.data.data;
		}

		const response = await apiClient.post<{ data: T }>(`/case-records/${data.id}/files`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});

		return response.data.data;
	},

	uploadComplaint: async (caseRecordId: string, data: FormData) => {
		const response = await apiClient.post<{ data: CaseRecord }>(`/case-records/${caseRecordId}/upload-complaint`, data, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
		return response.data.data;
	},
	uploadAnnex: async (caseRecordId: string, data: FormData) => {
		const response = await apiClient.post<{ data: CaseRecord }>(
			`/case-records/${caseRecordId.id}/upload-annex`,
			{ files: caseRecordId.files },
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
			},
		);
		return response.data.data;
	},

	generateRollo: async (caseRecordId: string) => {
		return await apiClient.post(`/generate_rollo/${caseRecordId}`);
	},

	deleteAnnex: async (caseRecordId: string | undefined, mediaId: string | undefined) => {
		await apiClient.delete(`/case-records/${caseRecordId}/annex/${mediaId}`);
	},

	deleteMedia: async (caseRecordId: string | undefined, mediaId: string | undefined) => {
		await apiClient.delete(`/case-records/${caseRecordId}/media/${mediaId}`);
	},

	getCaseStageChart: async (data: CaseStageChartSchema) => {
		const response = await apiClient.get("/backoffice/case-stage-charts");
		return response.data;
	},
	getTopCaseCategory: async (data: CaseStageChartSchema) => {
		const response = await apiClient.get("/backoffice/case-top-categories");
		return response.data;
	},
	preview: async (params = {}): Promise<Blob> => {
		const response = await apiClient.post("/backoffice/case-invite-download", params, {
			responseType: "blob",
		});

		return new Blob([response.data], { type: "application/pdf" });
	},
};

export default caseRecordService;
